(function HotelEngineBookingConditions($, Adzuki)
{
	'use strict';

	const component = {
		emits: ['close-modal'],
		props: {
			cancellationPolicy: {
				type   : Object,
				default: () => ({
					format    : 'list',
					policies  : [],
					text      : '',
					hasContent: false,
				}),
			},
			property: {
				type   : Object,
				default: null,
			},
			pets: {
				type   : [Array, Object],
				default: null,
			},
			additionalInfo: {
				type   : Object,
				default: null,
			},
		},
		template: `
			<div class="hotel-booking-conditions-modal">
				<template v-if="cancellationPolicy.hasContent">
					<h3 class="hotel-info-subtitle">Cancellation Policy</h3>
					<template v-if="cancellationPolicy.format === 'list'">
						<div
							v-for="(policy, index) in cancellationPolicy.policies"
							:key="'cancel-policy-' + index"
							class="hotel-booking-conditions-cancel-policy margin10-bottom"
						>
							<strong
								v-if="policy.start_date || policy.end_date"
								class="hotel-booking-summary-cancel-range"
							>{{ policy.start_date }} - {{ policy.end_date }}</strong>
							<div class="hotel-details-policy-text" v-html="policy.text"></div>
						</div>
					</template>
					<div
						v-else
						class="hotel-details-policy-text hotel-booking-conditions-cancel-policy"
						v-html="cancellationPolicy.text"
					></div>
				</template>

				<template v-if="hasPropertyContent">
					<h2
						class="hotel-info-section-title"
						:class="{'margin20-top': cancellationPolicy.hasContent}"
					>Hotel Information &amp; Policies</h2>

					<template v-if="propertyCheckInOut.length">
						<h3 class="hotel-info-subtitle">Check-in &amp; Check-out</h3>
						<ul class="hotel-details-policies-list">
							<li v-for="item in propertyCheckInOut" :key="item.key">
								<strong>{{ item.label }}:</strong>
								<div class="hotel-details-policy-text" v-html="item.value"></div>
							</li>
						</ul>
					</template>

					<template v-if="propertyDescriptions.length">
						<h3 class="hotel-info-subtitle">Descriptions</h3>
						<ul class="hotel-details-policies-list">
							<li v-for="item in propertyDescriptions" :key="item.key">
								<strong>{{ item.label }}:</strong>
								<div class="hotel-details-policy-text" v-html="item.value"></div>
							</li>
						</ul>
					</template>

					<template v-if="propertyFees.length">
						<h3 class="hotel-info-subtitle">Fees</h3>
						<ul class="hotel-details-policies-list">
							<li v-for="item in propertyFees" :key="item.key">
								<strong>{{ item.label }}:</strong>
								<div class="hotel-details-policy-text" v-html="item.value"></div>
							</li>
						</ul>
					</template>

					<template v-if="knowBeforeYouGoHtml || rateComments.length">
						<h3 class="hotel-info-subtitle">Know Before You Go</h3>
						<div
							v-if="knowBeforeYouGoHtml"
							class="hotel-details-policy-text"
							v-html="knowBeforeYouGoHtml"
						></div>
						<div v-if="rateComments.length" class="hotel-details-policy-text">
							<p
								v-for="(comment, index) in rateComments"
								:key="'rate-comment-' + index"
							>
								<span v-html="comment.description"></span>
							</p>
						</div>
					</template>

					<template v-if="hasPets">
						<h3 class="hotel-info-subtitle">Pet Policy</h3>
						<ul class="hotel-details-policies-list">
							<li v-for="(pet, idx) in petsList" :key="idx">
								{{ pet.raw_description }}
							</li>
						</ul>
					</template>
				</template>

				<p v-if="!cancellationPolicy.hasContent && !hasPropertyContent" class="text-muted">
					No booking condition details are available for this reservation.
				</p>
			</div>
		`,
		computed: {
			petsList() {
				if (!this.pets) {
					return [];
				}

				return Array.isArray(this.pets) ? this.pets : Object.values(this.pets);
			},
			hasPets() {
				return this.petsList.length > 0;
			},
			propertyCheckInOut() {
				return this.getPropertyItems([
					'checkin_time_start',
					'checkin_instructions',
					'checkin_special_instructions',
					'checkin_min_age',
					'checkout_time',
				]);
			},
			propertyDescriptions() {
				return this.getPropertyItems([
					'description_amenities',
					'description_dining',
					'description_renovations',
					'description_national_ratings',
					'description_business_amenities',
					'description_rooms',
					'description_attractions',
				]);
			},
			propertyFees() {
				return this.getPropertyItems([
					'fees_mandatory',
					'fees_optional',
				]);
			},
			propertyKnowBeforeYouGo() {
				return this.getPropertyItems([
					'policies_know_before_you_go'
				]);
			},
			additionalKnowBeforeYouGo() {
				return this.getAdditionalInfoItems();
			},
			rateComments() {
				const comments = this.additionalInfo?.rate_comments;

				if (!comments) {
					return [];
				}

				const list = Array.isArray(comments) ? comments : Object.values(comments);

				return list.filter((comment) => {
					return comment
						&& typeof comment === 'object'
						&& comment.description
						&& String(comment.description).trim();
				});
			},
			knowBeforeYouGoHtml() {
				return this.buildKnowBeforeYouGoHtml(
					this.propertyKnowBeforeYouGo,
					this.additionalKnowBeforeYouGo
				);
			},
			hasPropertyContent() {
				return this.propertyCheckInOut.length ||
					this.propertyDescriptions.length ||
					this.propertyFees.length ||
					this.knowBeforeYouGoHtml ||
					this.rateComments.length ||
					this.hasPets;
			},
		},
		methods: {
			getPropertyItems(keys) {
				const property = this.property;

				if (!property || typeof property !== 'object') {
					return [];
				}

				return keys.reduce((items, key) => {
					const entry = property[key];

					if (!entry) {
						return items;
					}

					const value = typeof entry === 'object' && entry !== null && 'value' in entry
						? entry.value
						: entry;

					if (!value || (typeof value === 'string' && !value.trim())) {
						return items;
					}

					const label = typeof entry === 'object' && entry !== null && entry.label
						? entry.label
						: key;

					items.push({
						key,
						label,
						value,
					});
					return items;
				}, []);
			},
			getAdditionalInfoItems() {
				const additionalInfo = this.additionalInfo;

				if (!additionalInfo || typeof additionalInfo !== 'object') {
					return [];
				}

				return Object.keys(additionalInfo).reduce((items, key) => {
					// rate_comments are rendered separately with v-html in Know Before You Go
					if (key === 'rate_comments') {
						return items;
					}

					const entry = additionalInfo[key];

					if (typeof entry === 'string' && entry.trim()) {
						items.push(entry.trim());
						return items;
					}

					if (Array.isArray(entry)) {
						entry.forEach((item) => {
							if (typeof item === 'string' && item.trim()) {
								items.push(item.trim());
								return;
							}

							if (item && typeof item === 'object' && item.description) {
								const description = String(item.description).trim();

								if (description) {
									items.push(description);
								}
							}
						});
					}

					return items;
				}, []);
			},
			buildKnowBeforeYouGoHtml(propertyItems, additionalItems) {
				const propertyHtml = propertyItems
					.map((item) => item.value)
					.filter(Boolean)
					.join('');

				if (!propertyHtml && !additionalItems.length) {
					return '';
				}

				const wrapper = document.createElement('div');

				if (propertyHtml) {
					wrapper.innerHTML = propertyHtml;
				}

				if (!additionalItems.length) {
					return wrapper.innerHTML;
				}

				let list = wrapper.querySelector('ul');

				if (!list) {
					list = document.createElement('ul');
					wrapper.appendChild(list);
				}

				additionalItems.forEach((text) => {
					const listItem = document.createElement('li');
					listItem.textContent = text;
					list.appendChild(listItem);
				});

				return wrapper.innerHTML;
			},
		},
	};

	Adzuki.VueComponents.HotelEngineBookingConditions = component;
}(jQuery, window.Adzuki || {}));
